"use client"
import { FragmentType, graphql, useFragment } from "@/gql"
import { useRef } from "react"
import { Spinner } from "@/elements"
import { useHandleListEndReached } from "@/hooks/useHandleListEndReached"
import { ApolloQueryResult, FetchMoreOptions, FetchMoreQueryOptions } from "@apollo/client"
import { JobsFeedItem, MobileJobsFeedItem } from "./JobsFeedItem"
import { Breakpoint } from "@/hooks/useBreakpoint"
import { UpsellCard } from "@/components/Subscriptions/UpsellCard"

const JobsFeedIndividualCreativeFragment = graphql(`
  fragment JobsFeedIndividualCreative on IndividualCreative {
    id
    proMembership {
      id
      activeMembershipType
    }
    ...UpsellCard
  }
`)

const JobsFeedJobPostingConnectionFragment = graphql(`
  fragment JobsFeedJobPostingConnection on JobPostingConnection {
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      cursor
      node {
        id
        __typename
        ...JobsFeedItem
        ...MobileJobsFeedItem
      }
    }
  }
`)

interface Props {
  jobPostings: FragmentType<typeof JobsFeedJobPostingConnectionFragment> | null | undefined
  creative: FragmentType<typeof JobsFeedIndividualCreativeFragment> | null | undefined
  selectedJobPostingId: string | null
  currentBreakpoint: Breakpoint | null
  onClickJobPosting: (
    id: string,
    type: "creative" | "client",
    hasImage: boolean,
    positionId: string,
    index: number,
    redacted: boolean
  ) => void
  loadingJobPosts: boolean
  networkStatus: number
  fetchMore?: (
    fetchMoreOptions: FetchMoreQueryOptions<any, any> & FetchMoreOptions<any, any>
  ) => Promise<ApolloQueryResult<any>>
  selectedIndex: number
  shouldHightlightSelectedIndex: boolean
  handleOpenCheckoutModal: () => void
}

export const JobsFeed: React.FC<Props> = ({
  onClickJobPosting,
  fetchMore,
  currentBreakpoint,
  loadingJobPosts,
  networkStatus,
  selectedIndex,
  shouldHightlightSelectedIndex,
  handleOpenCheckoutModal,
  ...props
}) => {
  const creative = useFragment(JobsFeedIndividualCreativeFragment, props.creative)
  const jobPostings = useFragment(JobsFeedJobPostingConnectionFragment, props.jobPostings)

  const loadMoreRef = useRef(null)
  useHandleListEndReached({
    pageInfo: jobPostings?.pageInfo,
    first: 10,
    networkStatus,
    fetchMore,
    observerRef: loadMoreRef,
  })

  const jobPosts = jobPostings?.edges

  if (!jobPosts && loadingJobPosts) {
    return (
      <div className="mb-3 mt-6 flex h-[25px] w-full flex-row items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const compactFeedItemBreakpoints = ["lg"]

  return (
    <div className="mx-auto flex w-full max-w-[920px] flex-col gap-y-3 pt-3 sm:gap-y-4 sm:pt-0">
      {creative?.proMembership?.activeMembershipType === null ? (
        <div className="px-5 sm:px-0">
          <UpsellCard creative={creative} handleOpenCheckoutModal={handleOpenCheckoutModal} />
        </div>
      ) : null}
      {jobPosts?.map((edge, index) => {
        const node = edge?.node
        if (!node) return null
        return (
          <div key={node.id} className="w-full">
            <div className="hidden w-full sm:flex">
              <JobsFeedItem
                jobPost={node}
                onClick={onClickJobPosting}
                jobIndex={index}
                selected={index === selectedIndex && shouldHightlightSelectedIndex}
                variant={
                  currentBreakpoint && compactFeedItemBreakpoints.includes(currentBreakpoint) ? "compact" : "default"
                }
              />
            </div>
            <div className="w-full px-5 sm:hidden">
              <MobileJobsFeedItem jobPost={node} onClick={onClickJobPosting} jobIndex={index} />
            </div>
          </div>
        )
      })}
      <div className="relative mb-3 mt-1 flex h-[25px] w-full flex-row items-center justify-center">
        {jobPostings?.pageInfo?.hasNextPage && <Spinner />}
      </div>
      <div ref={loadMoreRef} className="absolute bottom-[1100px] h-[5px] w-full" />
    </div>
  )
}
